import type { QueryClient } from '@tanstack/react-query';
import { notFound } from '@tanstack/react-router';
import { warmWorkbench } from '@paige/capturepoint';
import type { DataSource } from './data-source';
import { ApiError } from './http';
import type { JobDetail } from './types';
import { fleetQuery, jobQuery } from './queries';
import { billingQuery, companyQuery } from './company-queries';

/**
 * What a route has in hand before its screen shows (IMPLEMENTATION.md, sell-ready sprint 2,
 * row 13): the router keeps the screen the person is on until the loader settles, so the move
 * between the dashboard and a job is one transition, never an empty or half-drawn frame. The
 * reads land in the Query cache the screens' hooks then find warm.
 */
export interface LoaderContext {
  ds: DataSource;
  queryClient: QueryClient;
  /** Whether the signed-in person may read the company's billing (admins, managers, staff). */
  seesBilling?: boolean;
}

/**
 * The dashboard's reads: the jobs, and the company and billing that decide the line above them
 * (waiting for credits). The loader waits for all three and fails on none: a refused company read
 * still lets the jobs show, and jobs that cannot be read are a line on the home ("Jobs could not be
 * read just now"), where the bar, the bell and the way to Settings still work; thrown from here they
 * replaced the whole home with the failure screen (review 2026-10-06). Billing is read only for a
 * person the API lets read it (admins and managers, row 49 c); the screen decides.
 */
export async function loadDashboard(ctx: LoaderContext, tenant: string): Promise<void> {
  const { queryClient, ds } = ctx;
  await Promise.allSettled([
    queryClient.ensureQueryData(fleetQuery(ds, tenant)),
    queryClient.ensureQueryData(companyQuery(ds, tenant)),
    ...(ctx.seesBilling ? [queryClient.ensureQueryData(billingQuery(ds, tenant))] : []),
  ]);
}

/** The API has no such job for this person: deleted, never there, or not theirs to see. */
export const isMissing = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 404;

/** The review route's channel to the engine for a job (`/v1/jobs/{id}/engine` and `/pages`). */
export const engineOriginFor = (jobId: string): string => `/v1/jobs/${jobId}`;

/**
 * The job, then the engine's first answers for its workbench (the job list, the job's latest
 * batch overview) so the workbench opens on a warm channel. A job without an engine yet has
 * nothing to warm. An engine that refuses is not the loader's to report: the workbench shows it.
 * A job the API does not have is the 404 page, not a failure to retry (review 2026-10-06).
 */
export async function loadReview(
  ctx: LoaderContext,
  tenant: string,
  jobId: string,
): Promise<JobDetail> {
  const job = await ctx.queryClient
    .ensureQueryData(jobQuery(ctx.ds, tenant, jobId))
    .catch((error: unknown) => {
      throw isMissing(error) ? notFound() : error;
    });
  if (job.workspaceJobId) await warmWorkbench(engineOriginFor(jobId), tenant, job.workspaceJobId);
  return job;
}
